import Link from "@mui/material/Link";
import Guid from "_shared/guids/guid";
import { MarketplaceResultType } from "config";
import { getCompanyResultSummary } from "marketplace/_views/company-result-summary";
import MarketplaceChatCardContent from "marketplace/_views/marketplace-chat-card-content";
import MarketplaceProfileLink, { getMarketplaceProfilePath } from "marketplace/_views/marketplace-profile-link";
import MarketplaceResultCard, {
  MarketplaceResultAvatar,
  MarketplaceResultFact,
  MarketplaceResultFactList,
} from "marketplace/_views/marketplace-result-card";
import MarketplaceResultFooter from "marketplace/_views/marketplace-result-footer";
import CompanyBookmarkButton from "marketplace/bookmarks/_views/company-bookmark-button";
import {
  getCompanyAvatar,
} from "marketplace/companies/_store/companies-redux-slice";
import { companiesApi } from "marketplace/companies/_api/company-api";
import MarketplaceCompanyInfoSchema, { MarketplaceCompanyInfoJSON } from "marketplace/_schema/marketplace-company-info-schema";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import Forum from "messaging/forums/forum";
import React from "react";
import { useSelector } from "react-redux";

export interface CompanyResultProps {
  result: MarketplaceCompanyInfoJSON;
  getText: (fieldNames: string[], base: unknown, noCommas?: boolean) => string;
  handleResultClicked: (
    event: React.MouseEvent,
    profileId: Guid,
    profileType: MarketplaceResultType,
    component: React.JSX.Element,
    fullWidth: boolean,
  ) => void;
}

export default function CompanyResult(props: Readonly<CompanyResultProps>) {
  const { result, getText, handleResultClicked } = props;

  const companyId = result.id ? new Guid(result.id) : null;
  const companyProfile = result ? MarketplaceCompanyInfoSchema.deserializeToProfile(result) : undefined;
  const avatarId = result.avatarId;
  const companyName = getText(["name"], result, true);

  // #region State
  const [showChat, setShowChat] = React.useState<boolean>(false);
  const [forum, setForum] = React.useState<Forum>();

  // #region Selectors
  const avatar = useSelector(getCompanyAvatar(companyId, "medium"));
  companiesApi.useGetCompanyAvatarQuery(
    { companyId: companyId?.value ?? "", size: "medium" },
    { skip: !result.id || !!avatar || !avatarId }
  );
  const [getCompanyContactForum] = companiesApi.useGetCompanyContactForumMutation();

  const summary = getCompanyResultSummary(result);
  // The search sends the contact as a whole person and never fills `contactUserId`, so before #5410
  // the card's network and message buttons had nobody to act on for any company from a search.
  const contactUserId = result.contact?.userId ? new Guid(result.contact.userId) : companyProfile?.contactUserId;

  async function contactCompany() {
    if (!result?.id) throw new Error("Company ID is required to contact company.");
    try {
      const forumJson = await getCompanyContactForum({
        companyId: result.id,
      }).unwrap();
      setForum(Forum.fromJSON(forumJson));
      setShowChat(true);
    } catch (error) {
      console.error(error);
    }
  }

  // The avatar repeats the name's link for the mouse. The name is the one keyboard stop, so the
  // avatar stays out of the tab order and out of the accessibility tree.
  function linkAvatarToProfile(avatarElements: React.ReactNode) {
    if (!result.id) return avatarElements;
    const profileId = result.id;
    return (
      <MarketplaceProfileLink
        profileType={MarketplaceResultType.Company}
        profileId={profileId}
        onOpen={(event) => openCompanyProfile(event, profileId)}
        tabIndex={-1}
        aria-hidden="true"
        underline="none"
        sx={{ display: "contents" }}
      >
        {avatarElements}
      </MarketplaceProfileLink>
    );
  }

  function openCompanyProfile(event: React.MouseEvent, id: string | null | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Company,
      <ViewCompanyProfile companyId={profileId} />,
      true,
    );
  }

  function openIndividualProfile(event: React.MouseEvent, id: string | null | undefined) {
    if (!id) return;
    const profileId = new Guid(id);
    handleResultClicked(
      event,
      profileId,
      MarketplaceResultType.Individual,
      <ViewIndividualProfile individualId={profileId} />,
      true,
    );
  }

  function getWebsiteDetail(): React.ReactNode {
    if (!summary.website) return null;
    if (!summary.website.href) return summary.website.label;
    return (
      <Link href={summary.website.href} target="_blank" rel="noopener noreferrer" underline="hover">
        {summary.website.label}
      </Link>
    );
  }

  function getFacts(): MarketplaceResultFact[] {
    const facts: MarketplaceResultFact[] = [];
    const contact = result.contact;
    if (contact?.id && contact.firstName && contact.lastName) {
      const contactId = contact.id;
      facts.push({
        label: "Contact",
        value: (
          <MarketplaceProfileLink
            profileType={MarketplaceResultType.Individual}
            profileId={contactId}
            onOpen={(event) => openIndividualProfile(event, contactId)}
            underline="hover"
            noWrap
          >
            {getText(["firstName", "lastName"], contact, true)}
          </MarketplaceProfileLink>
        ),
      });
    }
    if (result.categories?.some(Boolean)) {
      facts.push({ label: "Categories", value: <MarketplaceResultFactList items={result.categories} /> });
    }
    return facts;
  }

  return (
    <React.Fragment>
      {showChat && (
        <MarketplaceChatCardContent>
          <ChatWindow fillAvailableSpace forums={forum ? [forum] : []} onClose={() => setShowChat(false)} />
        </MarketplaceChatCardContent>
      )}
      {!showChat && (
        <MarketplaceResultCard
          resultType={MarketplaceResultType.Company}
          label={companyName}
          avatar={linkAvatarToProfile(
            <MarketplaceResultAvatar resultType={MarketplaceResultType.Company} name={companyName} src={avatar} />,
          )}
          name={
            result.id ? (
              <MarketplaceProfileLink
                profileType={MarketplaceResultType.Company}
                profileId={result.id}
                onOpen={(event) => openCompanyProfile(event, result.id)}
                color="inherit"
                underline="always"
              >
                {companyName}
              </MarketplaceProfileLink>
            ) : (
              companyName
            )
          }
          details={[summary.location, getWebsiteDetail(), summary.size, summary.established]}
          headerAction={<CompanyBookmarkButton companyId={companyId ?? undefined} name={companyName} />}
          description={getText(["description"], result)}
          facts={getFacts()}
          footer={
            result.id && (
              <MarketplaceResultFooter
                resultName={companyName}
                profilePath={getMarketplaceProfilePath(MarketplaceResultType.Company, result.id)}
                networkUserId={contactUserId}
                onSendMessage={contactCompany}
              />
            )
          }
        />
      )}
    </React.Fragment>
  );
}
